import { z } from "zod";
import { MUSCLE_SLUGS } from "../bodymap.js";

// Per-user, per-muscle moving baseline of training volume. Lives in the
// single-table DDB under pk=USER#<sub>, sk=BASELINE#muscle#<slug>.
// Recomputed by the SessionResult Lambda on every session-write so the
// Progress dashboard's Tier 1 "+60% vs 28d avg" alert can fire without
// re-scanning all of the user's history at read time.
//
// The three windows serve distinct roles:
//   - avg7dKg     → short-term load (acute fatigue signal)
//   - avg28dKg    → medium-term baseline (chronic load comparator)
//   - baselineKg  → 90-day trimmed mean, slow-drifting reference
//                   (excludes top + bottom decile so a single PR
//                    session doesn't lift the bar; the alert compares
//                    avg28dKg against this).
//
// Severity thresholds (consumed by compareToBaseline in progress.ts):
//   ok       avg28dKg ≤ 1.30 × baselineKg
//   warn     1.30  < ratio ≤ 1.60
//   alarm    ratio > 1.60   (≈ "+60% vs baseline" — the canonical line)
//
// Backfill is mandatory before the dashboard surfaces alerts; without
// it the first 28 days of any user account would show false positives
// against a near-zero baseline. See progress-redesign.md §8 Phase 3.
export const MuscleBaseline = z.object({
  userSub: z.string().min(1).max(128),
  muscleSlug: z.enum(MUSCLE_SLUGS),
  avg7dKg: z.number().min(0),
  avg28dKg: z.number().min(0),
  baselineKg: z.number().min(0),
  // Total number of distinct days that contributed to baselineKg.
  // Drives a "warming up" badge in the UI: until sampleCountDays ≥ 28
  // we render the baseline as informational only, not alert-grade.
  sampleCountDays: z.number().int().min(0),
  // Last time the baseline writer touched this row. ISO-8601 UTC, ms.
  lastUpdatedAt: z.iso.datetime(),
});
export type MuscleBaseline = z.infer<typeof MuscleBaseline>;
